import type { DriveProfile } from '../types/drive';

const BASE_PROFILES: Record<string, DriveProfile> = {
  G120: {
    id: 'G120',
    name: 'SINAMICS G120 (0.75 kW)',
    vendor: 'SIEMENS',
    family: 'SINAMICS G120',
    modelCode: '6SL3210-1PE21-0UL0',
    powerKW: 0.75,
    voltageClass: '380V',
    ratedFrequency: 60,
    coolingMethod: 'forced-air',
    protectionRating: 'IP20',
    fanCount: 1,
    nominalCurrent: 2.3,
    nominalDC: 560,
    maxTemp: 90,
    currentScale: 100,
    regFreq: 40022,
    regCurrent: 40025,
    regDC: 40024,
    regTemp: 40029,
    regWriteFreq: 40101,
    poles: 4,
    rulParams: { fanMainLife: 50000, fanControlLife: 30000, capacitorBaseLife: 80000, igbtMaxCycles: 50000, bearingBaseLife: 40000 },
    tripCodes: {
      OC: { code: 'F30001', mask: 0x0001, desc: 'Overcurrent (F30001)' },
      OV: { code: 'F30002', mask: 0x0002, desc: 'DC Overvoltage (F30002)' },
      UV: { code: 'F30003', mask: 0x0004, desc: 'DC Undervoltage (F30003)' },
      OT: { code: 'F30004', mask: 0x0008, desc: 'Heatsink Overtemp (F30004)' },
      FAN: { code: 'F30035', mask: 0x0010, desc: 'Cabinet Fan Defect (F30035)' },
    },
  },

  G130: {
    id: 'G130',
    name: 'SINAMICS G130 Chassis (250 kW)',
    vendor: 'SIEMENS',
    family: 'SINAMICS G130',
    modelCode: '6SL3310-1GE41-4AA3',
    powerKW: 250,
    voltageClass: '380V',
    ratedFrequency: 60,
    coolingMethod: 'forced-air',
    protectionRating: 'IP20',
    fanCount: 2,
    nominalCurrent: 470.0,
    nominalDC: 560,
    maxTemp: 85,
    currentScale: 10,
    regFreq: 40022,
    regCurrent: 40025,
    regDC: 40024,
    regTemp: 40029,
    regWriteFreq: 40101,
    poles: 4,
    rulParams: { fanMainLife: 60000, fanControlLife: 35000, capacitorBaseLife: 90000, igbtMaxCycles: 45000, bearingBaseLife: 50000 },
    tripCodes: {
      OC: { code: 'F30001', mask: 0x0001, desc: 'Overcurrent (F30001)' },
      OV: { code: 'F30002', mask: 0x0002, desc: 'DC Overvoltage (F30002)' },
      UV: { code: 'F30003', mask: 0x0004, desc: 'DC Undervoltage (F30003)' },
      OT: { code: 'F30004', mask: 0x0008, desc: 'Power Unit Overtemp (F30004)' },
      FAN: { code: 'F30035', mask: 0x0010, desc: 'Chassis Fan Failure (F30035)' },
    },
  },

  G150: {
    id: 'G150',
    name: 'SINAMICS G150 Cabinet (500 kW)',
    vendor: 'SIEMENS',
    family: 'SINAMICS G150',
    modelCode: '6SL3710-1GE38-4AA3',
    powerKW: 500,
    voltageClass: '380V',
    ratedFrequency: 60,
    coolingMethod: 'forced-air',
    protectionRating: 'IP20',
    fanCount: 3,
    nominalCurrent: 910.0,
    nominalDC: 560,
    maxTemp: 80,
    currentScale: 10,
    regFreq: 40022,
    regCurrent: 40025,
    regDC: 40024,
    regTemp: 40029,
    regWriteFreq: 40101,
    poles: 4,
    rulParams: { fanMainLife: 70000, fanControlLife: 40000, capacitorBaseLife: 100000, igbtMaxCycles: 40000, bearingBaseLife: 60000 },
    tripCodes: {
      OC: { code: 'F30001', mask: 0x0001, desc: 'Overcurrent (F30001)' },
      OV: { code: 'F30002', mask: 0x0002, desc: 'DC Overvoltage (F30002)' },
      UV: { code: 'F30003', mask: 0x0004, desc: 'DC Undervoltage (F30003)' },
      OT: { code: 'F30004', mask: 0x0008, desc: 'Cabinet Overtemp (F30004)' },
      FAN: { code: 'F30035', mask: 0x0010, desc: 'Exhaust Fan Defect (F30035)' },
    },
  },

  FC302: {
    id: 'FC302',
    name: 'Danfoss VLT FC-302 (32 A)',
    vendor: 'DANFOSS',
    family: 'VLT FC-302',
    modelCode: 'FC-302P32K0T5E20H1XG',
    powerKW: 15,
    voltageClass: '380V',
    ratedFrequency: 60,
    coolingMethod: 'forced-air',
    protectionRating: 'IP20',
    fanCount: 1,
    nominalCurrent: 32.0,
    nominalDC: 540,
    maxTemp: 90,
    currentScale: 10,
    regFreq: 1612,
    regCurrent: 1613,
    regDC: 1614,
    regTemp: 1633,
    regWriteFreq: 1612,
    poles: 4,
    rulParams: { fanMainLife: 50000, fanControlLife: 30000, capacitorBaseLife: 70000, igbtMaxCycles: 50000, bearingBaseLife: 40000 },
    tripCodes: {
      OC: { code: 'A13', mask: 0x0001, desc: 'Overcurrent (Alarm 13)' },
      OV: { code: 'A7', mask: 0x0002, desc: 'DC Overvoltage (Alarm 7)' },
      UV: { code: 'A8', mask: 0x0004, desc: 'DC Undervoltage (Alarm 8)' },
      OT: { code: 'A29', mask: 0x0008, desc: 'Heatsink Overtemp (Alarm 29)' },
      FAN: { code: 'A69', mask: 0x0010, desc: 'Fan Fault (Alarm 69)' },
    },
  },

  'CFW11-010': {
    id: 'CFW11-010',
    name: 'WEG CFW-11 (380-480 V / 10 A - 5 cv)',
    vendor: 'WEG',
    family: 'CFW-11',
    modelCode: 'CFW11010T5D20ENZ',
    frameSize: 'A',
    fanCode: '10194998',
    powerCardCode: '10194958',
    capacitorBankCode: '10194988',
    powerKW: 4,
    voltageClass: '380V',
    ratedFrequency: 60,
    coolingMethod: 'forced-air',
    protectionRating: 'IP20',
    fanCount: 1,
    nominalCurrent: 10.0,
    nominalDC: 540,
    maxTemp: 85,
    currentScale: 10,
    regFreq: 5,
    regCurrent: 3,
    regDC: 4,
    regTemp: 30,
    regWriteFreq: 683,
    poles: 4,
    rulParams: { fanMainLife: 50000, fanControlLife: 25000, capacitorBaseLife: 80000, igbtMaxCycles: 50000, bearingBaseLife: 40000 },
    tripCodes: {
      OC: { code: 'F048', mask: 0x0001, desc: 'Overcurrent (Fault 48)' },
      OV: { code: 'F071', mask: 0x0002, desc: 'DC Overvoltage (Fault 71)' },
      UV: { code: 'F072', mask: 0x0004, desc: 'DC Undervoltage (Fault 72)' },
      OT: { code: 'F051', mask: 0x0008, desc: 'IGBT Overtemp (Fault 51)' },
      FAN: { code: 'F163', mask: 0x0010, desc: 'Fan Fault (Fault 163)' },
    },
  },

  'CFW11-070': {
    id: 'CFW11-070',
    name: 'WEG CFW-11 (380-480 V / 70 A - 50 cv)',
    vendor: 'WEG',
    family: 'CFW-11',
    modelCode: 'CFW11070T5D20ENZ',
    frameSize: 'B',
    fanCode: '10194998',
    powerCardCode: '10194960',
    capacitorBankCode: '10194991',
    powerKW: 37,
    voltageClass: '380V',
    ratedFrequency: 60,
    coolingMethod: 'forced-air',
    protectionRating: 'IP20',
    fanCount: 1,
    nominalCurrent: 70.0,
    nominalDC: 540,
    maxTemp: 85,
    currentScale: 10,
    regFreq: 5,
    regCurrent: 3,
    regDC: 4,
    regTemp: 30,
    regWriteFreq: 683,
    poles: 4,
    rulParams: { fanMainLife: 50000, fanControlLife: 25000, capacitorBaseLife: 80000, igbtMaxCycles: 50000, bearingBaseLife: 40000 },
    tripCodes: {
      OC: { code: 'F048', mask: 0x0001, desc: 'Overcurrent (Fault 48)' },
      OV: { code: 'F071', mask: 0x0002, desc: 'DC Overvoltage (Fault 71)' },
      UV: { code: 'F072', mask: 0x0004, desc: 'DC Undervoltage (Fault 72)' },
      OT: { code: 'F051', mask: 0x0008, desc: 'IGBT Overtemp (Fault 51)' },
      FAN: { code: 'F163', mask: 0x0010, desc: 'Fan Fault (Fault 163)' },
    },
  },

  'CFW11-370': {
    id: 'CFW11-370',
    name: 'WEG CFW-11 (380-480 V / 370 A - 300 cv)',
    vendor: 'WEG',
    family: 'CFW-11',
    modelCode: 'CFW11370T5D20ENZ',
    frameSize: 'H',
    fanCode: '12295303',
    powerKW: 220,
    voltageClass: '380V',
    ratedFrequency: 60,
    coolingMethod: 'forced-air',
    protectionRating: 'IP20',
    fanCount: 2,
    nominalCurrent: 370.0,
    nominalDC: 540,
    maxTemp: 85,
    currentScale: 10,
    regFreq: 5,
    regCurrent: 3,
    regDC: 4,
    regTemp: 30,
    regWriteFreq: 683,
    poles: 4,
    rulParams: { fanMainLife: 60000, fanControlLife: 30000, capacitorBaseLife: 90000, igbtMaxCycles: 40000, bearingBaseLife: 50000 },
    tripCodes: {
      OC: { code: 'F048', mask: 0x0001, desc: 'Overcurrent (Fault 48)' },
      OV: { code: 'F071', mask: 0x0002, desc: 'DC Overvoltage (Fault 71)' },
      UV: { code: 'F072', mask: 0x0004, desc: 'DC Undervoltage (Fault 72)' },
      OT: { code: 'F051', mask: 0x0008, desc: 'IGBT Overtemp (Fault 51)' },
      FAN: { code: 'F163', mask: 0x0010, desc: 'Fan Fault (Fault 163)' },
    },
  },

  PF525: {
    id: 'PF525',
    name: 'Rockwell PowerFlex 525 (4 kW / 480 V)',
    vendor: 'ROCKWELL',
    family: 'PF525',
    modelCode: '25B-D010N104',
    powerKW: 4,
    voltageClass: '480V',
    ratedFrequency: 60,
    coolingMethod: 'forced-air',
    protectionRating: 'IP20',
    fanCount: 1,
    nominalCurrent: 10.5,
    nominalDC: 678,
    maxTemp: 90,
    currentScale: 100,
    regFreq: 8451,
    regCurrent: 8452,
    regDC: 8453,
    regTemp: 27,
    regWriteFreq: 8193,
    poles: 4,
    rulParams: { fanMainLife: 50000, fanControlLife: 30000, capacitorBaseLife: 80000, igbtMaxCycles: 50000, bearingBaseLife: 40000 },
    tripCodes: {
      OC: { code: 'F012', mask: 0x0001, desc: 'HW OverCurrent (Fault 12)' },
      OV: { code: 'F005', mask: 0x0002, desc: 'OverVoltage - DC Bus (Fault 5)' },
      UV: { code: 'F004', mask: 0x0004, desc: 'UnderVoltage - DC Bus (Fault 4)' },
      OT: { code: 'F008', mask: 0x0008, desc: 'Heatsink OvrTmp (Fault 8)' },
      FAN: { code: 'F009', mask: 0x0010, desc: 'CC OvrTmp (Fault 9) - check control module fan/airflow' },
    },
  },
};

export const DRIVE_PROFILES: Record<string, DriveProfile> = { ...BASE_PROFILES };

export function getProfile(key: string): DriveProfile | undefined {
  return DRIVE_PROFILES[key];
}

export function setDynamicProfile(key: string, profile: DriveProfile): void {
  DRIVE_PROFILES[key] = profile;
}

export function removeDynamicProfile(key: string): void {
  delete DRIVE_PROFILES[key];
}

export const PROFILE_KEYS: string[] = Object.keys(DRIVE_PROFILES);
